(() => {
  const container = document.getElementById("korvelTaxBrowser");
  if (!container) return;

  const ASSET_URL = container.dataset.korvelCatalogsUrl || "";
  const GRID_VIEW = "korvel_grid";

  const CSS_PRICE = container.dataset.korvelCssPriceUrl || "";
  const CSS_PRODUCT_CARD = container.dataset.korvelCssProductCardUrl || "";
  const JS_QUICK_ADD = container.dataset.korvelJsQuickAddUrl || "";

  // Section Rendering API
  const GRID_SECTION_NAME = (container.dataset.korvelGridSection || "").trim();

  // Grid freshness policy: collection/product grids should not stay stale after catalog updates.
  const GRID_FETCH_CACHE_MODE = "no-store";
  const GRID_CACHE_TTL_MS = 0; // 0 = disable in-memory grid cache


  const state = {
    catalogs: null,

    // LRU cache for grids (disabled by default for live collection freshness):
    // key = `${handle}::${page}::${mode}::${section}`
    // value = { html, ts }
    gridCache: new Map(),

    // per-root request tracking and abort
    reqIdByRoot: new Map(),
    abortByRoot: new Map(),

    initializedUI: new Set(),
    ensuredAssets: false,

    // ✅ Mobile swipe pagination
    swipeBound: new Set(),
    lastSwipeAtByRoot: new Map(),

    // Lazy load roots
    io: null,
    ioObserved: new Set(),
    ioLoaded: new Set(),
    prefetching: new Set()
  };

  // ---------- utilities ----------
  function safeText(v) { return (v == null) ? "" : String(v); }
  function escHtml(s) {
    return safeText(s)
      .replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;")
      .replace(/"/g,"&quot;").replace(/'/g,"&#039;");
  }
  function normKey(s){ return safeText(s).toLowerCase().replace(/\s+/g, " ").trim(); }

  // ---------- locale ----------
  const SUPPORTED_LOCALES = ["en","es","fr"];
  const SUPPORTED_LOCALES_SET = new Set(SUPPORTED_LOCALES);

  function detectLocaleAndPrefix(){
    const path = (window.location && window.location.pathname) ? window.location.pathname : "/";
    const seg1 = (path.split("/").filter(Boolean)[0] || "").toLowerCase();
    const prefix = SUPPORTED_LOCALES_SET.has(seg1) ? `/${seg1}` : "";

    const htmlLangRaw = (document.documentElement && document.documentElement.getAttribute("lang"))
      ? document.documentElement.getAttribute("lang")
      : "";
    const htmlSeg = String(htmlLangRaw).toLowerCase().split(/[-_]/)[0];

    const locale = SUPPORTED_LOCALES_SET.has(htmlSeg)
      ? htmlSeg
      : (SUPPORTED_LOCALES_SET.has(seg1) ? seg1 : "en");

    return { locale, prefix };
  }

  const { locale: KORVEL_LOCALE, prefix: KORVEL_LOCALE_PREFIX } = detectLocaleAndPrefix();

  const UI_I18N = {
    en: { reset: "Reset", showAll: "Show all", loading: "Loading products…", failed: "Failed to load." },
    es: { reset: "Restablecer", showAll: "Ver todo", loading: "Cargando productos…", failed: "No se pudo cargar." },
    fr: { reset: "Réinitialiser", showAll: "Tout afficher", loading: "Chargement des produits…", failed: "Échec du chargement." }
  };

  function tUI(key){
    const pack = UI_I18N[KORVEL_LOCALE] || UI_I18N.en;
    return (pack && pack[key]) || (UI_I18N.en && UI_I18N.en[key]) || key;
  }

  function withLocalePrefix(path){
    const p = safeText(path).trim();
    if (!p) return p;
    if (p[0] !== "/") return p;
    if (!KORVEL_LOCALE_PREFIX) return p;
    if (p === KORVEL_LOCALE_PREFIX || p.startsWith(KORVEL_LOCALE_PREFIX + "/")) return p;
    return KORVEL_LOCALE_PREFIX + p;
  }

  function collectionUrl(handle){
    if (!handle) return "#";
    return withLocalePrefix(`/collections/${encodeURIComponent(String(handle))}`);
  }

  // ---------- theme assets ensure-once ----------
  function ensureStyleOnce(href){
    try{
      if (!href) return;
      const exists = Array.from(document.querySelectorAll('link[rel="stylesheet"]'))
        .some(l => (l.getAttribute('href') || "") === href);
      if (exists) return;
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = href;
      document.head.appendChild(link);
    }catch(_){}
  }

  function ensureScriptOnce(src){
    return new Promise((resolve) => {
      try{
        if (!src) return resolve();
        const exists = Array.from(document.querySelectorAll('script[src]'))
          .some(s => (s.getAttribute('src') || "") === src);
        if (exists) return resolve();
        const s = document.createElement("script");
        s.src = src;
        s.defer = true;
        s.onload = () => resolve();
        s.onerror = () => resolve();
        document.head.appendChild(s);
      }catch(_){
        resolve();
      }
    });
  }

  function ensureThemeAssets(){
    if (state.ensuredAssets) return Promise.resolve();
    state.ensuredAssets = true;
    ensureStyleOnce(CSS_PRICE);
    ensureStyleOnce(CSS_PRODUCT_CARD);
    return ensureScriptOnce(JS_QUICK_ADD);
  }

  // ---------- JSON fetch (allow browser cache) ----------
  async function fetchJSON(url){
    const r = await fetch(url, { cache: "force-cache" });
    if (!r.ok) throw new Error(`Failed to fetch JSON: ${r.status}`);
    return await r.json();
  }

  // ---------- i18n helpers ----------
  function pickI18n(i18nObj){
    if (!i18nObj || typeof i18nObj !== "object") return "";
    const loc = KORVEL_LOCALE;
    let v = i18nObj[loc] || i18nObj[loc.toUpperCase()];
    if (typeof v === "string" && v.trim()) return v;

    for (const k in i18nObj){
      const base = String(k).toLowerCase().split(/[-_]/)[0];
      if (base === loc){
        const vv = i18nObj[k];
        if (typeof vv === "string" && vv.trim()) return vv;
      }
    }
    v = i18nObj.en || i18nObj.EN;
    if (typeof v === "string" && v.trim()) return v;

    for (const k in i18nObj){
      const vv = i18nObj[k];
      if (typeof vv === "string" && vv.trim()) return vv;
    }
    return "";
  }

  function nodeLabel(node){
    if (!node) return "";
    const candidate =
      pickI18n(node.title_i18n) ||
      pickI18n(node.name_i18n) ||
      pickI18n(node.label_i18n) ||
      pickI18n(node.full_name_i18n) ||
      safeText(node.title || node.name || node.label || node.full_name || "");
    const out = safeText(candidate).trim();
    if (out) return out;

    const h =
      (typeof node.handle === "string" && node.handle.trim()) ? node.handle.trim() :
      (typeof node.collection_handle === "string" && node.collection_handle.trim()) ? node.collection_handle.trim() :
      "";
    return h || "—";
  }

  // ---------- catalog tree helpers ----------
  function getHandle(node){
    if (!node) return null;
    if (typeof node.handle === "string" && node.handle.trim()) return node.handle.trim();
    if (typeof node.collection_handle === "string" && node.collection_handle.trim()) return node.collection_handle.trim();
    if (node.collection && typeof node.collection.handle === "string" && node.collection.handle.trim()) return node.collection.handle.trim();
    if (typeof node.collectionHandle === "string" && node.collectionHandle.trim()) return node.collectionHandle.trim();

    const url = node.url || node.link || node.href;
    if (typeof url === "string"){
      const m = url.match(/\/collections\/([^\/\?\#]+)/i);
      if (m && m[1]) return m[1];
    }
    return null;
  }

  function childrenOf(node){
    const c = node && (node.children || node.items || node.nodes || node.subcategories);
    return Array.isArray(c) ? c : [];
  }

  function firstHandleInSubtree(node){
    const h = getHandle(node);
    if (h) return h;
    for (const k of childrenOf(node)){
      const hh = firstHandleInSubtree(k);
      if (hh) return hh;
    }
    return null;
  }

  function hasHandleInSubtree(node){
    if (getHandle(node)) return true;
    for (const k of childrenOf(node)){
      if (hasHandleInSubtree(k)) return true;
    }
    return false;
  }

  // ---------- banners (kept inline to preserve behavior) ----------
  const ROOT_TITLE_BANNERS = {
    "animals & pet supplies": {
      en: "https://cdn.shopify.com/s/files/1/0733/2698/5493/files/ANIMALS_PET_SUPPLIES.png?v=1769520854",
      es: "https://cdn.shopify.com/s/files/1/0733/2698/5493/files/Productos_para_mascotas_y_animales.png?v=1770291147",
      fr: "https://cdn.shopify.com/s/files/1/0733/2698/5493/files/Animaux_et_articles_pour_animaux_de_compagnie.png?v=1770666489"
    },
    "apparel & accessories": {
      en: "https://cdn.shopify.com/s/files/1/0733/2698/5493/files/APPAREL_ACCESSORIES_39c4c94b-c975-404e-8358-bed22837323f.png?v=1769520873",
      es: "https://cdn.shopify.com/s/files/1/0733/2698/5493/files/Ropa_y_accesorio.png?v=1770291228",
      fr: "https://cdn.shopify.com/s/files/1/0733/2698/5493/files/Vetements_et_accessoires.png?v=1770666600"
    },
    "arts & entertainment": {
      en: "https://cdn.shopify.com/s/files/1/0733/2698/5493/files/ARTS_ENTERTAINMENT.png?v=1769521311",
      es: "https://cdn.shopify.com/s/files/1/0733/2698/5493/files/Arte_y_ocio.png?v=1770291283",
      fr: "https://cdn.shopify.com/s/files/1/0733/2698/5493/files/Arts_et_loisirs.png?v=1770666681"
    },
    "baby & toddler": {
      en: "https://cdn.shopify.com/s/files/1/0733/2698/5493/files/BABY_TODDLER.png?v=1769521377",
      es: "https://cdn.shopify.com/s/files/1/0733/2698/5493/files/Bebes_y_ninos_pequenos.png?v=1770291340",
      fr: "https://cdn.shopify.com/s/files/1/0733/2698/5493/files/Bebes_et_tout-petits.png?v=1770666741"
    },
    "business & industrial": {
      en: "https://cdn.shopify.com/s/files/1/0733/2698/5493/files/BUSINESS_INDUSTRIAL.png?v=1769521707",
      es: "https://cdn.shopify.com/s/files/1/0733/2698/5493/files/Economia_e_industria.png?v=1770291422",
      fr: "https://cdn.shopify.com/s/files/1/0733/2698/5493/files/Entreprise_et_industrie.png?v=1770666786"
    },
    "cameras & optics": {
      en: "https://cdn.shopify.com/s/files/1/0733/2698/5493/files/CAMERA_OPTICS.png?v=1769521832",
      es: "https://cdn.shopify.com/s/files/1/0733/2698/5493/files/Camaras_y_opticas.png?v=1770291466",
      fr: "https://cdn.shopify.com/s/files/1/0733/2698/5493/files/Appareils_photo_cameras_et_instruments_d_optique.png?v=1770666909"
    },
    "food, beverages & tobacco": {
      en: "https://cdn.shopify.com/s/files/1/0733/2698/5493/files/FOOD_BEVERAGES_TOBACCO.png?v=1769520854",
      es: "https://cdn.shopify.com/s/files/1/0733/2698/5493/files/Alimentacion_bebida_y_tabaco.png?v=1770291040",
      fr: "https://cdn.shopify.com/s/files/1/0733/2698/5493/files/Alimentation_boissons_et_tabac.png?v=1770667032"
    },
    "furniture": {
      en: "https://cdn.shopify.com/s/files/1/0733/2698/5493/files/FURNITURE.png?v=1769522186",
      es: "https://cdn.shopify.com/s/files/1/0733/2698/5493/files/Mobiliario.png?v=1770291664",
      fr: "https://cdn.shopify.com/s/files/1/0733/2698/5493/files/Meubles.png?v=1770667122"
    },
    "health & beauty": {
      en: "https://cdn.shopify.com/s/files/1/0733/2698/5493/files/HEALTH_BEAUTY_8ef50401-d5e0-422d-be01-b31ad57eb392.png?v=1769522373",
      es: "https://cdn.shopify.com/s/files/1/0733/2698/5493/files/Salud_y_belleza.png?v=1770292454",
      fr: "https://cdn.shopify.com/s/files/1/0733/2698/5493/files/Sante_et_beaute.png?v=1770667302"
    },
    "home & garden": {
      en: "https://cdn.shopify.com/s/files/1/0733/2698/5493/files/HOME_GARDEN_c97c5842-d954-4acd-b885-520388c91026.png?v=1769522418",
      es: "https://cdn.shopify.com/s/files/1/0733/2698/5493/files/Casa_y_jardin.png?v=1770292256",
      fr: "https://cdn.shopify.com/s/files/1/0733/2698/5493/files/Maison_et_jardin.png?v=1770667358"
    },
    "luggage & bags": {
      en: "https://cdn.shopify.com/s/files/1/0733/2698/5493/files/LUGGAGE_BAGS_1bad8f30-7a4f-482e-b965-8bfec3a4d05d.png?v=1769522466",
      es: "https://cdn.shopify.com/s/files/1/0733/2698/5493/files/Maletas_y_bolsos_de_viaje.png?v=1770292363",
      fr: "https://cdn.shopify.com/s/files/1/0733/2698/5493/files/Bagages_et_maroquinerie.png?v=1770667425"
    },
    "mature": {
      en: "https://cdn.shopify.com/s/files/1/0733/2698/5493/files/MATURE.png?v=1769522571",
      es: "https://cdn.shopify.com/s/files/1/0733/2698/5493/files/Maduro.png?v=1770303351",
      fr: "https://cdn.shopify.com/s/files/1/0733/2698/5493/files/mature_1.png?v=1770667504"
    },
    "sporting goods": {
      en: "https://cdn.shopify.com/s/files/1/0733/2698/5493/files/SPORTING_GOODS.png?v=1769522836",
      es: "https://cdn.shopify.com/s/files/1/0733/2698/5493/files/Equipamiento_deportivo.png?v=1770301387",
      fr: "https://cdn.shopify.com/s/files/1/0733/2698/5493/files/Equipements_sportifs.png?v=1770667725"
    },
    "electronics": {
      en: "https://cdn.shopify.com/s/files/1/0733/2698/5493/files/ELECTRONICS.png?v=1769522008",
      es: "https://cdn.shopify.com/s/files/1/0733/2698/5493/files/Electronica.png?v=1770291512",
      fr: "https://cdn.shopify.com/s/files/1/0733/2698/5493/files/Appareil_electronique.png?v=1770666969"
    },
    "toys & games": {
      en: "https://cdn.shopify.com/s/files/1/0733/2698/5493/files/TOYS_GAMES.png?v=1769523001",
      es: "https://cdn.shopify.com/s/files/1/0733/2698/5493/files/Juegos_y_juguetes.png?v=1770302058",
      fr: "https://cdn.shopify.com/s/files/1/0733/2698/5493/files/Jeux_et_jouets.png?v=1770667783"
    },
    "hardware": {
      en: "https://cdn.shopify.com/s/files/1/0733/2698/5493/files/HARDWARE_efd1e7e7-faca-430d-9c6d-9eaa1629462e.png?v=1769521485",
      es: "https://cdn.shopify.com/s/files/1/0733/2698/5493/files/Hardware_1.png?v=1770303209",
      fr: "https://cdn.shopify.com/s/files/1/0733/2698/5493/files/Quincaillerie.png?v=1770667255"
    },
    "media": {
      en: "https://cdn.shopify.com/s/files/1/0733/2698/5493/files/MEDIA.png?v=1769611127",
      es: "https://cdn.shopify.com/s/files/1/0733/2698/5493/files/Medios_de_comunicacion.png?v=1770303466",
      fr: "https://cdn.shopify.com/s/files/1/0733/2698/5493/files/Medias.png?v=1770667544"
    },
    "office supplies": {
      en: "https://cdn.shopify.com/s/files/1/0733/2698/5493/files/OFFICE_SUPPLIES.png?v=1769611264",
      es: "https://cdn.shopify.com/s/files/1/0733/2698/5493/files/Material_de_oficina.png?v=1770301861",
      fr: "https://cdn.shopify.com/s/files/1/0733/2698/5493/files/Fournitures_de_bureau.png?v=1770667592"
    },
    "religious & ceremonial": {
      en: "https://cdn.shopify.com/s/files/1/0733/2698/5493/files/religious_and_ceremonil.png?v=1776847630",
      es: "https://cdn.shopify.com/s/files/1/0733/2698/5493/files/Elementos_religiosos_y_ceremoniales.png?v=1770301903",
      fr: "https://cdn.shopify.com/s/files/1/0733/2698/5493/files/Offices_religieux_et_ceremonies.png?v=1770667921"
    },
    "software": {
      en: "https://cdn.shopify.com/s/files/1/0733/2698/5493/files/SOFTWARE.png?v=1769611489",
      es: "https://cdn.shopify.com/s/files/1/0733/2698/5493/files/Software_1.png?v=1770301985",
      fr: "https://cdn.shopify.com/s/files/1/0733/2698/5493/files/Logiciels.png?v=1770667979"
    },
    "vehicles & parts": {
      en: "https://cdn.shopify.com/s/files/1/0733/2698/5493/files/VEHICLES_PARTS_267f5192-5e8f-416c-a42e-be40c0844293.png?v=1769523051",
      es: "https://cdn.shopify.com/s/files/1/0733/2698/5493/files/Vehiculos_y_recambios.png?v=1770302091",
      fr: "https://cdn.shopify.com/s/files/1/0733/2698/5493/files/Vehicules_et_accessoires.png?v=1770667834"
    },
    "gift cards": {
      en: "https://cdn.shopify.com/s/files/1/0733/2698/5493/files/GIFT_CARDS.png?v=1769522316",
      es: "https://cdn.shopify.com/s/files/1/0733/2698/5493/files/Tarjetas_de_regalo.png?v=1770302163",
      fr: "https://cdn.shopify.com/s/files/1/0733/2698/5493/files/Cartes-cadeaux.png?v=1770667197"
    },
    "services": {
      en: "https://cdn.shopify.com/s/files/1/0733/2698/5493/files/SERVICES.png?v=1770302758",
      es: "https://cdn.shopify.com/s/files/1/0733/2698/5493/files/Servicios.png?v=1770303623",
      fr: "https://cdn.shopify.com/s/files/1/0733/2698/5493/files/Services_1.png?v=1770667670"
    }
  };

  function getRootBannerUrl(root){
    const title = safeText(root && (root.title || root.name || root.label)).trim();
    const handle = safeText(getHandle(root)).trim();
    const tKey = normKey(title);
    const hKey = normKey(handle);
    const entry =
      (hKey && ROOT_TITLE_BANNERS[hKey]) ||
      (tKey && ROOT_TITLE_BANNERS[tKey]) ||
      null;
    if (!entry) return null;
    if (typeof entry === "string") return entry;
    if (entry && typeof entry === "object"){
      const loc = KORVEL_LOCALE;
      let v = entry[loc] || entry[loc.toUpperCase()] || entry.en || entry.EN;
      if (typeof v === "string" && v.trim()) return v;
      for (const k in entry){
        const vv = entry[k];
        if (typeof vv === "string" && vv.trim()) return vv;
      }
    }
    return null;
  }

  // ---------- markup builders ----------
  function buildRootHeading(root, rootIndex){
    const titleText = nodeLabel(root) || safeText(root.title || root.name || root.label || `Catalog ${rootIndex+1}`).trim();
    const titleEsc  = escHtml(titleText);
    const bannerUrl = getRootBannerUrl(root);
    const handle = getHandle(root) || firstHandleInSubtree(root);
    const href = handle ? collectionUrl(handle) : "#";
    const hrefEsc = escHtml(href);

    if (!bannerUrl){
      return `
        <h2 class="korvel-root-title">
          ${handle ? `<a class="korvel-root-title__link" href="${hrefEsc}">${titleEsc}</a>` : titleEsc}
        </h2>
      `;
    }

    const urlEsc = escHtml(bannerUrl);
    return `
      <div class="korvel-root-banner">
        <a class="korvel-root-banner__link" href="${hrefEsc}" aria-label="${titleEsc}">
          <img src="${urlEsc}" alt="${titleEsc}" loading="lazy" decoding="async">
        </a>
      </div>
      <h2 class="korvel-root-title korvel-sr-only">
        <a class="korvel-root-title__link" href="${hrefEsc}">${titleEsc}</a>
      </h2>
    `;
  }

  function buildPills(nodes, level, rootIndex){
    if (!nodes || !nodes.length) return "";
    return `
      <div class="korvel-level korvel-level-${level}" data-root-index="${rootIndex}" data-level="${level}">
        <div class="korvel-pills">
          ${nodes.map((n, idx) => {
            const label = escHtml(nodeLabel(n));
            return `<button class="korvel-pill" type="button"
              data-root-index="${rootIndex}"
              data-level="${level}"
              data-index="${idx}">${label}</button>`;
          }).join("")}
        </div>
      </div>
    `;
  }

  function buildEmptyLevel(level, rootIndex){
    return `<div class="korvel-level korvel-level-${level}" data-root-index="${rootIndex}" data-level="${level}" style="display:none"></div>`;
  }

  function buildFilterBar(rootIndex){
    return `
      <div class="korvel-filterbar" id="korvelFilterBar_${rootIndex}">
        <nav class="korvel-breadcrumbs" id="korvelBreadcrumbs_${rootIndex}" aria-label="Breadcrumb"></nav>
        <a href="#" class="korvel-filter-reset" data-root-index="${rootIndex}">${escHtml(tUI("reset"))}</a>
      </div>
    `;
  }

  function buildRootBlock(root, rootIndex){
    const l1 = childrenOf(root);
    const initialMsg = tUI("loading");
    return `
      <section class="korvel-root-block" data-root-index="${rootIndex}">
        <div class="korvel-root-head">
          ${buildRootHeading(root, rootIndex)}
        </div>
        <div class="korvel-sublevels">
          ${buildFilterBar(rootIndex)}
          ${buildPills(l1, 1, rootIndex)}
          ${buildEmptyLevel(2, rootIndex)}
          ${buildEmptyLevel(3, rootIndex)}
        </div>
        <div class="korvel-grid-wrap" id="korvelGridWrap_${rootIndex}">
          <div class="korvel-grid-placeholder">${escHtml(initialMsg)}</div>
        </div>
        <div class="korvel-show-all">
          <a href="#" class="korvel-show-all-link" data-root-index="${rootIndex}">${escHtml(tUI("showAll"))}</a>
        </div>
      </section>
    `;
  }

  // ---------- UI state helpers ----------
  function setActivePill(rootIndex, level, index){
    const pills = container.querySelectorAll(`.korvel-pill[data-root-index="${rootIndex}"][data-level="${level}"]`);
    pills.forEach(p => p.classList.remove("is-active"));
    const active = container.querySelector(`.korvel-pill[data-root-index="${rootIndex}"][data-level="${level}"][data-index="${index}"]`);
    if (active) active.classList.add("is-active");
  }

  function showLevel(rootIndex, level, nodes){
    const el = container.querySelector(`.korvel-level-${level}[data-root-index="${rootIndex}"]`);
    if (!el) return;
    if (!nodes || !nodes.length){
      el.style.display = "none";
      el.innerHTML = "";
      return;
    }
    el.style.display = "block";
    el.innerHTML = `
      <div class="korvel-pills">
        ${nodes.map((n, idx) => {
          const label = escHtml(nodeLabel(n));
          return `<button class="korvel-pill" type="button"
            data-root-index="${rootIndex}"
            data-level="${level}"
            data-index="${idx}">${label}</button>`;
        }).join("")}
      </div>
    `;
  }

  function updateBreadcrumbs(rootIndex){
    const bcEl = document.getElementById(`korvelBreadcrumbs_${rootIndex}`);
    if (!bcEl) return;

    const l1 = container.querySelector(`.korvel-pill.is-active[data-root-index="${rootIndex}"][data-level="1"]`);
    const l2 = container.querySelector(`.korvel-pill.is-active[data-root-index="${rootIndex}"][data-level="2"]`);
    const l3 = container.querySelector(`.korvel-pill.is-active[data-root-index="${rootIndex}"][data-level="3"]`);

    const labels = [l1, l2, l3]
      .filter(Boolean)
      .map(el => safeText(el.textContent).trim())
      .filter(Boolean);

    if (!labels.length){
      bcEl.innerHTML = "";
      return;
    }

    bcEl.innerHTML = labels.map((t, i) => {
      const item = `<span class="korvel-breadcrumbs__item">${escHtml(t)}</span>`;
      const sep = (i < labels.length - 1) ? `<span class="korvel-breadcrumbs__sep">›</span>` : "";
      return item + sep;
    }).join(" ");
  }

  function clearActivePills(rootIndex, level){
    container
      .querySelectorAll(`.korvel-pill.is-active[data-root-index="${rootIndex}"][data-level="${level}"]`)
      .forEach(el => el.classList.remove("is-active"));
  }

  function getDefaultHandle(root){
    return getHandle(root) || firstHandleInSubtree(root);
  }

  function initRootUI(root, rootIndex){
    if (state.initializedUI.has(rootIndex)) return;
    clearActivePills(rootIndex, 1);
    clearActivePills(rootIndex, 2);
    clearActivePills(rootIndex, 3);
    showLevel(rootIndex, 2, []);
    showLevel(rootIndex, 3, []);
    updateBreadcrumbs(rootIndex);
    state.initializedUI.add(rootIndex);
  }

  function getSelectedHandle(root, rootIndex){
    const l1 = childrenOf(root);
    const a1 = container.querySelector(`.korvel-pill.is-active[data-root-index="${rootIndex}"][data-level="1"]`);
    if (!a1) return getHandle(root) || firstHandleInSubtree(root);

    const i1 = Number(a1.dataset.index);
    const n1 = l1[i1] || null;
    if (!n1) return getHandle(root) || firstHandleInSubtree(root);

    const l2 = childrenOf(n1);
    const a2 = container.querySelector(`.korvel-pill.is-active[data-root-index="${rootIndex}"][data-level="2"]`);
    if (!a2) return getHandle(n1) || firstHandleInSubtree(n1) || getHandle(root) || firstHandleInSubtree(root);

    const i2 = Number(a2.dataset.index);
    const n2 = l2[i2] || null;
    if (!n2) return getHandle(n1) || firstHandleInSubtree(n1) || getHandle(root) || firstHandleInSubtree(root);

    const l3 = childrenOf(n2);
    const a3 = container.querySelector(`.korvel-pill.is-active[data-root-index="${rootIndex}"][data-level="3"]`);
    if (!a3) return getHandle(n2) || firstHandleInSubtree(n2) || getHandle(n1) || firstHandleInSubtree(n1) || getHandle(root) || firstHandleInSubtree(root);

    const i3 = Number(a3.dataset.index);
    const n3 = l3[i3] || null;
    const activeNode = n3 || n2 || n1 || root;
    return getHandle(activeNode)
      || firstHandleInSubtree(activeNode)
      || getHandle(root)
      || firstHandleInSubtree(root);
  }

  // ---------- pagination helpers ----------
  function getPageFromHref(href){
    try{
      const u = new URL(href, window.location.origin);
      const p = Number(u.searchParams.get("page") || "1");
      return (p && p > 0) ? p : 1;
    }catch(_){
      return 1;
    }
  }

  function normalizePaginationHref(href){
    try{
      const u = new URL(href, window.location.origin);
      u.searchParams.delete("view");
      u.pathname = withLocalePrefix(u.pathname);
      return u.pathname + (u.search || "") + (u.hash || "");
    }catch(_){
      return href;
    }
  }

  function fixPaginationLinks(wrap){
    if (!wrap) return;
    const links = wrap.querySelectorAll(".pagination a[href], nav.pagination a[href], [class*='pagination'] a[href], [class*='paginate'] a[href]");
    links.forEach(a => {
      const href = a.getAttribute("href");
      if (!href) return;
      if (!/([?&])page=\d+/i.test(href)) return;
      a.setAttribute("href", normalizePaginationHref(href));
    });
  }

  // ---------- swipe pagination ----------
  const korvelDelay = (ms) => new Promise(r => setTimeout(r, ms));
  const korvelNextFrame = () => new Promise(r => requestAnimationFrame(r));

  function korvelResetSwipeStyles(wrap){
    if (!wrap) return;
    wrap.style.transform = "";
    wrap.style.opacity = "";
    wrap.style.transition = "";
    wrap.style.willChange = "";
  }

  function korvelDragStyles(wrap, dx){
    if (!wrap) return;
    const w = Math.max(280, (wrap.getBoundingClientRect && wrap.getBoundingClientRect().width) ? wrap.getBoundingClientRect().width : 0);
    const clamp = Math.min(w * 0.55, 240);
    const x = Math.max(-clamp, Math.min(clamp, dx));
    const op = String(1 - Math.min(0.22, Math.abs(x) / (clamp * 1.6)));
    wrap.style.transition = "none";
    wrap.style.willChange = "transform,opacity";
    wrap.style.transform = `translate3d(${x}px,0,0)`;
    wrap.style.opacity = op;
  }

  async function korvelSnapBack(wrap){
    if (!wrap) return;
    wrap.style.willChange = "transform,opacity";
    wrap.style.transition = "transform 180ms ease, opacity 180ms ease";
    wrap.style.transform = "translate3d(0,0,0)";
    wrap.style.opacity = "1";
    await korvelDelay(190);
    korvelResetSwipeStyles(wrap);
  }

  async function korvelSwipeAnimateLoad(wrap, dir, loadFn){
    if (!wrap) return;
    const w = Math.max(280, (wrap.getBoundingClientRect && wrap.getBoundingClientRect().width) ? wrap.getBoundingClientRect().width : 0);
    const out = (dir === "next" ? -1 : 1) * Math.min(240, Math.round(w * 0.35));
    wrap.style.willChange = "transform,opacity";
    wrap.style.transition = "transform 140ms ease, opacity 140ms ease";
    wrap.style.transform = `translate3d(${out}px,0,0)`;
    wrap.style.opacity = "0.35";
    await korvelDelay(150);
    await loadFn();
    wrap.style.transition = "none";
    wrap.style.transform = `translate3d(${-out}px,0,0)`;
    wrap.style.opacity = "0.35";
    await korvelNextFrame();
    wrap.style.transition = "transform 190ms ease, opacity 190ms ease";
    wrap.style.transform = "translate3d(0,0,0)";
    wrap.style.opacity = "1";
    await korvelDelay(210);
    korvelResetSwipeStyles(wrap);
  }

  function findPagerHref(wrap, dir){
    if (!wrap) return null;
    const selNext = [
      'a[rel="next"]',
      'a[aria-label*="Next"]',
      'a[aria-label*="next"]',
      '.pagination__item--next a',
      '.pagination__next a',
      '.pagination a.next',
      'nav.pagination a.next'
    ].join(',');

    const selPrev = [
      'a[rel="prev"]',
      'a[aria-label*="Previous"]',
      'a[aria-label*="previous"]',
      '.pagination__item--prev a',
      '.pagination__prev a',
      '.pagination a.prev',
      'nav.pagination a.prev'
    ].join(',');

    const a = wrap.querySelector(dir === "next" ? selNext : selPrev);
    return a ? (a.getAttribute("href") || null) : null;
  }

  async function navigateBySwipe(rootIndex, dir){
    const now = Date.now();
    const last = state.lastSwipeAtByRoot.get(rootIndex) || 0;
    if (now - last < 450) return;
    state.lastSwipeAtByRoot.set(rootIndex, now);

    const wrap = document.getElementById(`korvelGridWrap_${rootIndex}`);
    if (!wrap || wrap.classList.contains("is-loading")) return;

    const href = findPagerHref(wrap, dir);
    if (!href){
      await korvelSnapBack(wrap);
      return;
    }

    const root = state.catalogs && state.catalogs.roots ? state.catalogs.roots[rootIndex] : null;
    if (!root){
      await korvelSnapBack(wrap);
      return;
    }

    initRootUI(root, rootIndex);
    const handle = getSelectedHandle(root, rootIndex) || getDefaultHandle(root);
    const page = getPageFromHref(href);

    await korvelSwipeAnimateLoad(wrap, dir, async () => {
      await loadGridInto(rootIndex, handle, page);
    });
  }

  function bindSwipeForRoot(rootIndex){
    if (state.swipeBound.has(rootIndex)) return;

    const wrap = document.getElementById(`korvelGridWrap_${rootIndex}`);
    if (!wrap) return;

    let sx = 0, sy = 0, dx = 0, dy = 0;
    let tracking = false;
    let locked = false;

    const THRESH_LOCK = 10;
    const THRESH_SWIPE = 45;

    wrap.addEventListener("touchstart", (e) => {
      if (!e.touches || e.touches.length !== 1) return;
      if (wrap.classList.contains("is-loading")) return;
      tracking = true;
      locked = false;
      dx = dy = 0;
      sx = e.touches[0].clientX;
      sy = e.touches[0].clientY;
      korvelResetSwipeStyles(wrap);
    }, { passive: true });

    wrap.addEventListener("touchmove", (e) => {
      if (!tracking || !e.touches || e.touches.length !== 1) return;
      dx = e.touches[0].clientX - sx;
      dy = e.touches[0].clientY - sy;
      if (!locked){
        if (Math.abs(dx) > Math.abs(dy) + THRESH_LOCK) locked = true;
      }
      if (locked){
        e.preventDefault();
        korvelDragStyles(wrap, dx);
      }
    }, { passive: false });

    wrap.addEventListener("touchend", () => {
      if (!tracking) return;
      tracking = false;
      if (!locked) return;
      if (Math.abs(dx) >= THRESH_SWIPE && Math.abs(dx) > Math.abs(dy) * 1.2){
        const dir = (dx < 0) ? "next" : "prev";
        void navigateBySwipe(rootIndex, dir);
      } else {
        void korvelSnapBack(wrap);
      }
    }, { passive: true });

    wrap.addEventListener("touchcancel", () => {
      if (!tracking) return;
      tracking = false;
      void korvelSnapBack(wrap);
    }, { passive: true });

    state.swipeBound.add(rootIndex);
  }

  // ---------- LRU cache ----------
  const LRU_MAX = 50;

  function lruGet(key){
    if (GRID_CACHE_TTL_MS <= 0) return null;
    if (!state.gridCache.has(key)) return null;

    const entry = state.gridCache.get(key);
    if (!entry || typeof entry !== "object") {
      state.gridCache.delete(key);
      return null;
    }

    if ((Date.now() - Number(entry.ts || 0)) > GRID_CACHE_TTL_MS){
      state.gridCache.delete(key);
      return null;
    }

    // refresh
    state.gridCache.delete(key);
    state.gridCache.set(key, entry);
    return entry.html || null;
  }

  function lruSet(key, val){
    if (GRID_CACHE_TTL_MS <= 0) return;
    if (state.gridCache.has(key)) state.gridCache.delete(key);
    state.gridCache.set(key, { html: val, ts: Date.now() });
    while (state.gridCache.size > LRU_MAX){
      const firstKey = state.gridCache.keys().next().value;
      state.gridCache.delete(firstKey);
    }
  }

  function appendFreshParam(url){
    try{
      const u = new URL(url, window.location.origin);
      u.searchParams.set('_ktb', String(Date.now()));
      return u.toString();
    }catch(_){
      const sep = String(url).includes('?') ? '&' : '?';
      return `${url}${sep}_ktb=${Date.now()}`;
    }
  }

  // ---------- grid extraction ----------
  function extractGridHTML(text){
    const parser = new DOMParser();
    const doc = parser.parseFromString(text, "text/html");
    const root = doc.querySelector("#korvelGridRoot") || doc.body;
    if (!root) return text;
    const gridContainer =
      root.querySelector("#ProductGridContainer") ||
      root.querySelector("#product-grid") ||
      root;
    return gridContainer.outerHTML || gridContainer.innerHTML || root.innerHTML || text;
  }

  // ---------- Section Rendering API fetch ----------
  async function fetchGridHTMLBySections(handle, page, signal){
    if (!GRID_SECTION_NAME) return null;
    if (!handle) return null;

    const p = (typeof page === "number" && Number.isFinite(page) && page > 1) ? Math.floor(page) : 1;

    // IMPORTANT: do NOT include ?view=... for sections endpoint
    // Use locale-prefixed collection URL
    // Shopify supports `?sections=...` and page param for collection pagination
    const pageParam = (p > 1) ? `&page=${encodeURIComponent(String(p))}` : "";
    const url = appendFreshParam(`${collectionUrl(handle)}?sections=${encodeURIComponent(GRID_SECTION_NAME)}${pageParam}`);

    const cacheKey = `${handle}::${p}::sections::${GRID_SECTION_NAME}`;
    const cached = lruGet(cacheKey);
    if (cached != null) return cached;

    const r = await fetch(url, { cache: GRID_FETCH_CACHE_MODE, signal });
    if (!r.ok) return null;

    const j = await r.json();
    const html = j && j[GRID_SECTION_NAME] ? String(j[GRID_SECTION_NAME]) : "";
    if (!html) return null;

    // Extract only the grid container from the section HTML (safe)
    const extracted = extractGridHTML(html);
    lruSet(cacheKey, extracted);
    return extracted;
  }

  // ---------- fallback: ?view=korvel_grid ----------
  async function fetchGridHTMLByView(handle, page, signal){
    if (!handle) return `<div class="korvel-grid-placeholder">No collection handle.</div>`;
    const p = (typeof page === "number" && Number.isFinite(page) && page > 1) ? Math.floor(page) : 1;

    const cacheKey = `${handle}::${p}::view::${GRID_VIEW}`;
    const cached = lruGet(cacheKey);
    if (cached != null) return cached;

    const pageParam = (p > 1) ? `&page=${encodeURIComponent(String(p))}` : "";
    const url = appendFreshParam(`${collectionUrl(handle)}?view=${encodeURIComponent(GRID_VIEW)}${pageParam}`);

    const r = await fetch(url, { cache: GRID_FETCH_CACHE_MODE, signal });
    const text = await r.text();
    const html = extractGridHTML(text);
    lruSet(cacheKey, html);
    return html;
  }

  async function fetchGridHTML(handle, page, signal){
    // Try Section Rendering API first (faster, less parsing of full page)
    try{
      const viaSections = await fetchGridHTMLBySections(handle, page, signal);
      if (viaSections) return viaSections;
    }catch(_){}
    // Fallback to the old view mechanism
    return await fetchGridHTMLByView(handle, page, signal);
  }

  // ---------- uniquify optimization ----------
function uniquifyGridMarkup(wrap, rootIndex){
  if (!wrap) return;

  const suffix = `korvel-${rootIndex}-${Date.now()}`;

  // Fix duplicate ids (cheap + always safe)
  wrap.querySelectorAll('#ProductGridContainer').forEach(el => el.id = `ProductGridContainer-${suffix}`);
  wrap.querySelectorAll('#product-grid').forEach(el => el.id = `product-grid-${suffix}`);

  // If no section data-id, nothing else to rewrite
  const dataIdEl = wrap.querySelector('[data-id]');
  const oldSectionId = dataIdEl ? (dataIdEl.getAttribute('data-id') || '') : '';
  if (!oldSectionId) return;

  const newSectionId = `${oldSectionId}-${suffix}`;

  // ✅ Fast path: если oldSectionId встречается в HTML только один раз (обычно только в data-id),
  // то не делаем дорогой проход по DOM.
  try{
    const html = wrap.innerHTML || '';
    const first = html.indexOf(oldSectionId);
    const last  = html.lastIndexOf(oldSectionId);
    const occursMoreThanOnce = (first !== -1 && last !== -1 && first !== last);

    // Всегда обновляем data-id
    dataIdEl.setAttribute('data-id', newSectionId);

    if (!occursMoreThanOnce){
      return;
    }
  }catch(_){
    // если что-то пошло не так — просто продолжим к полному проходу
    dataIdEl.setAttribute('data-id', newSectionId);
  }

  const ATTR_OK = (name) => {
    if (!name) return false;
    if (name === 'id' || name === 'for') return true;
    if (name.startsWith('aria-')) return true;
    if (name.startsWith('data-')) return true;
    return false;
  };

  // ✅ Full path (optimized): используем getElementsByTagName вместо querySelectorAll('*')
  const all = wrap.getElementsByTagName('*');
  for (let i = 0; i < all.length; i++){
    const el = all[i];
    const attrs = el.attributes;
    if (!attrs) continue;

    for (let j = 0; j < attrs.length; j++){
      const attr = attrs[j];
      if (!ATTR_OK(attr.name)) continue;

      const v = attr.value || '';
      if (v && v.indexOf(oldSectionId) !== -1){
        el.setAttribute(attr.name, v.split(oldSectionId).join(newSectionId));
      }
    }
  }
}


  function pokeThemeInit(wrap){
    try{
      const ev = new CustomEvent('shopify:section:load', { bubbles: true });
      wrap.dispatchEvent(ev);
      document.dispatchEvent(ev);
    }catch(_){}
  }

  // ---------- core: load grid ----------
  async function loadGridInto(rootIndex, handle, page){
    const wrap = document.getElementById(`korvelGridWrap_${rootIndex}`);
    if (!wrap) return;

    const p = (typeof page === "number" && Number.isFinite(page) && page > 1) ? Math.floor(page) : 1;

    // bump request id + abort previous
    const nextId = (state.reqIdByRoot.get(rootIndex) || 0) + 1;
    state.reqIdByRoot.set(rootIndex, nextId);

    const prevAbort = state.abortByRoot.get(rootIndex);
    if (prevAbort) {
      try { prevAbort.abort(); } catch(_) {}
    }
    const ac = new AbortController();
    state.abortByRoot.set(rootIndex, ac);

    wrap.classList.add("is-loading");
    await new Promise(r => requestAnimationFrame(r));

    try {
      await ensureThemeAssets();

      const html = await fetchGridHTML(handle, p, ac.signal);

      if (state.reqIdByRoot.get(rootIndex) !== nextId) return;

      wrap.innerHTML = html;

      // Uniquify only what is needed
      uniquifyGridMarkup(wrap, rootIndex);

      // Fix pagination links so clicking doesn't open the partial view page
      fixPaginationLinks(wrap);

      pokeThemeInit(wrap);
    } catch (e){
      if (state.reqIdByRoot.get(rootIndex) !== nextId) return;
      if (e && (e.name === "AbortError")) return;
      wrap.innerHTML = `<div class="korvel-grid-placeholder">${escHtml(tUI("failed"))}</div>`;
    } finally {
      if (state.reqIdByRoot.get(rootIndex) === nextId){
        wrap.classList.remove("is-loading");
      }
    }

    const link = container.querySelector(`.korvel-show-all-link[data-root-index="${rootIndex}"]`);
    if (link && handle) link.href = collectionUrl(handle);
  }

  // ---------- progressive root loading via IntersectionObserver ----------
  function ensureIO(roots){
    if (state.io) return;

    const preloadMargin = "240px"; // prefetch a bit before it becomes visible
    state.io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const el = entry.target;
        const rootIndex = Number(el.getAttribute("data-root-index"));
        if (!Number.isFinite(rootIndex)) return;

        // load once
        if (state.ioLoaded.has(rootIndex)) return;
        state.ioLoaded.add(rootIndex);

        const root = roots[rootIndex];
        if (!root) return;

        initRootUI(root, rootIndex);
        const handle = getDefaultHandle(root);
        if (handle) void loadGridInto(rootIndex, handle, 1);

        // Light prefetch next root when current becomes visible
        const next = rootIndex + 1;
        if (next < roots.length && !state.prefetching.has(next) && !state.ioLoaded.has(next)){
          state.prefetching.add(next);
          const nextRoot = roots[next];
          const nextHandle = nextRoot ? getDefaultHandle(nextRoot) : null;
          if (nextHandle){
            // warm cache with sections or view
            // using a dummy AbortController (do not store per-root)
            const ac = new AbortController();
            fetchGridHTML(nextHandle, 1, ac.signal).catch(() => {}).finally(() => {
              state.prefetching.delete(next);
            });
          } else {
            state.prefetching.delete(next);
          }
        }
      });
    }, { root: null, rootMargin: preloadMargin, threshold: 0.01 });
  }

  function observeRootsForLazyLoad(roots){
    ensureIO(roots);

    // Load immediately only first 1-2 roots
    const immediate = Math.min(2, roots.length);
    for (let i = 0; i < immediate; i++){
      if (state.ioLoaded.has(i)) continue;
      state.ioLoaded.add(i);
      initRootUI(roots[i], i);
      const handle = getDefaultHandle(roots[i]);
      if (handle) void loadGridInto(i, handle, 1);
    }

    // Observe remaining roots for viewport-based loading
    for (let i = 0; i < roots.length; i++){
      const el = container.querySelector(`.korvel-root-block[data-root-index="${i}"]`);
      if (!el) continue;
      if (state.ioObserved.has(i)) continue;
      state.io.observe(el);
      state.ioObserved.add(i);
    }
  }

  // ---------- click handling ----------
  function handleContainerClick(e){
    const showAll = e.target.closest(".korvel-show-all-link");
    if (showAll) return;

    // Pagination links inside the injected grid: do AJAX paginate instead of navigating
    const pagelink = e.target.closest(".korvel-grid-wrap a[href]");
    if (pagelink){
      const isPager = pagelink.closest(".pagination, nav.pagination, [class*='pagination'], [class*='paginate']");
      const href = pagelink.getAttribute("href") || "";
      if (isPager && /([?&])page=\d+/i.test(href)){
        e.preventDefault();
        const rootBlock = pagelink.closest(".korvel-root-block");
        const rootIndex = rootBlock ? Number(rootBlock.dataset.rootIndex) : NaN;
        const root = Number.isFinite(rootIndex) ? (state.catalogs && state.catalogs.roots ? state.catalogs.roots[rootIndex] : null) : null;
        if (!root) return;
        initRootUI(root, rootIndex);
        const handle = getSelectedHandle(root, rootIndex) || getDefaultHandle(root);
        const page = getPageFromHref(href);
        void loadGridInto(rootIndex, handle, page);
        return;
      }
    }

    const reset = e.target.closest(".korvel-filter-reset");
    if (reset){
      e.preventDefault();
      const rootIndex = Number(reset.dataset.rootIndex);
      const root = state.catalogs.roots[rootIndex];
      if (!root) return;
      state.initializedUI.delete(rootIndex);
      initRootUI(root, rootIndex);
      const handle = getDefaultHandle(root);
      void loadGridInto(rootIndex, handle, 1);
      return;
    }

    const pill = e.target.closest(".korvel-pill");
    if (!pill) return;

    e.preventDefault();
    const rootIndex = Number(pill.dataset.rootIndex);
    const level = Number(pill.dataset.level);
    const index = Number(pill.dataset.index);
    const root = state.catalogs.roots[rootIndex];
    if (!root) return;

    initRootUI(root, rootIndex);
    setActivePill(rootIndex, level, index);

    const l1 = childrenOf(root);

    if (level === 1){
      const n1 = l1[index] || null;
      const l2nodes = n1 ? childrenOf(n1) : [];
      showLevel(rootIndex, 2, l2nodes);
      clearActivePills(rootIndex, 2);
      showLevel(rootIndex, 3, []);
      clearActivePills(rootIndex, 3);
    }

    if (level === 2){
      const a1 = container.querySelector(`.korvel-pill.is-active[data-root-index="${rootIndex}"][data-level="1"]`);
      const i1 = a1 ? Number(a1.dataset.index) : 0;
      const n1 = l1[i1] || null;
      const l2nodes = n1 ? childrenOf(n1) : [];
      const n2 = l2nodes[index] || null;
      const l3nodes = n2 ? childrenOf(n2) : [];
      showLevel(rootIndex, 3, l3nodes);
      clearActivePills(rootIndex, 3);
    }

    updateBreadcrumbs(rootIndex);

    const handle = getSelectedHandle(root, rootIndex);
    void loadGridInto(rootIndex, handle, 1);
  }

  // ---------- boot ----------
  async function boot(){
    try{
      const raw = await fetchJSON(ASSET_URL);
      const roots = (function normalize(data){
        if (!data) return [];
        if (Array.isArray(data.roots)) return data.roots;
        if (data.catalogs && Array.isArray(data.catalogs.roots)) return data.catalogs.roots;
        if (Array.isArray(data.items)) return data.items;
        if (Array.isArray(data.nodes)) return data.nodes;
        if (typeof data === "object") return [data];
        return [];
      })(raw);

      const usableRoots = (roots || []).filter(r => hasHandleInSubtree(r));
      if (!usableRoots.length){
        container.innerHTML = `<div class="korvel-grid-placeholder">No catalog roots found.</div>`;
        return;
      }

      state.catalogs = { roots: usableRoots };

      container.innerHTML = usableRoots.map((r, i) => buildRootBlock(r, i)).join("");

      container.addEventListener("click", handleContainerClick);

      for (let i = 0; i < usableRoots.length; i++){
        initRootUI(usableRoots[i], i);
        bindSwipeForRoot(i);
      }

      // Lazy load: only 1-2 roots immediately, others by viewport
      observeRootsForLazyLoad(usableRoots);

    } catch (err){
      container.innerHTML = `<div class="korvel-grid-placeholder">Error: ${escHtml(err && err.message ? err.message : String(err))}</div>`;
    }
  }

  boot();
})();
